/* global globalThis */
/**
 * @fileoverview Add Note Modal Component
 * @module components/modals/AddNoteModal
 * @description A modal component for adding and editing notes with support for different note types,
 * program associations, ratings, and pinning functionality.
 *
 * @requires react
 * @requires @mui/material
 * @requires @mui/icons-material/Close
 * @requires @mui/icons-material/ExpandMore
 * @requires @mui/icons-material/PushPin
 * @requires ../../assets/mdi_pin.svg
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import mdiPin from "../../assets/mdi_pin.svg";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { placeholderText } from "../../constants/colors";
import PushPinIcon from "@mui/icons-material/PushPin";
import ArchiveIcon from "@mui/icons-material/Archive";

/** Styled MUI Select component with custom border radius */
// Removed unused StyledSelect component
/**
 * AddNoteModal Component
 *
 * A modal dialog for creating and editing notes with various types and configurations.
 * Supports different note types, program associations, ratings, and pinning functionality.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSave - Callback when note is saved
 * @param {boolean} props.hasPinnedNote - Whether a pinned note already exists
 * @param {Object} [props.initialData] - Initial note data for editing
 * @param {Object} [props.programOptions] - Available program options for association
 * @returns {JSX.Element} The rendered modal component
 */
const HeaderActions = ({
  isAddNote,
  isArchived,
  handleToggleArchive,
  canDelete,
  isPinned,
  handlePinClick,
  hasPinnedNote,
  initialData,
}) => {
  if (isAddNote) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <ArchiveAction
          isArchived={isArchived}
          handleToggleArchive={handleToggleArchive}
          canDelete={canDelete}
        />
        <PinAction
          isPinned={isPinned}
          handlePinClick={handlePinClick}
          hasPinnedNote={hasPinnedNote}
          initialData={initialData}
        />
      </Box>
    </Box>
  );
};

HeaderActions.propTypes = {
  isAddNote: PropTypes.bool.isRequired,
  isArchived: PropTypes.bool.isRequired,
  handleToggleArchive: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
  isPinned: PropTypes.bool.isRequired,
  handlePinClick: PropTypes.func.isRequired,
  hasPinnedNote: PropTypes.bool,
  initialData: PropTypes.object,
};

const ArchiveAction = ({ isArchived, handleToggleArchive, canDelete }) => (
  <Box sx={{ display: "flex", alignItems: "center" }}>
    <IconButton
      aria-label={isArchived ? "unarchive note" : "archive note"}
      onClick={handleToggleArchive}
      disabled={!canDelete}
      sx={{
        padding: "4px",
        color: isArchived ? "#403C6F" : "rgba(64, 60, 111, 0.7)",
        marginLeft: "-8px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "transparent",
        },
        "&.Mui-disabled": {
          cursor: "not-allowed",
          color: "#999893",
          pointerEvents: "auto !important",
        },
      }}
    >
      <ArchiveIcon
        style={{
          width: "20px",
          height: "20px",
          opacity: isArchived ? 1 : 0.5,
        }}
      />
    </IconButton>
    <Box
      sx={{
        fontFamily: "Manrope, sans-serif",
        fontSize: "14px",
        color: isArchived ? "#403C6F" : "rgba(64, 60, 111, 0.8)",
        fontWeight: isArchived ? 600 : 500,
        ml: "4px",
      }}
    >
      {isArchived ? "Unarchive" : "Archive"}
    </Box>
  </Box>
);

ArchiveAction.propTypes = {
  isArchived: PropTypes.bool.isRequired,
  handleToggleArchive: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
};

const PinAction = ({
  isPinned,
  handlePinClick,
  hasPinnedNote,
  initialData,
}) => {
  if (hasPinnedNote && !initialData?.pinNote) {
    return (
      <Tooltip
        title="Unpin the current note to pin a new one"
        placement="bottom"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: "#333333",
              "& .MuiTooltip-arrow": {
                color: "#333333",
              },
              fontSize: "12px",
              padding: "8px 12px",
              borderRadius: "4px",
              maxWidth: "200px",
            },
          },
          popper: {
            modifiers: [
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  altAxis: true,
                  altBoundary: true,
                  tether: true,
                  rootBoundary: "document",
                  padding: 8,
                },
              },
            ],
            disablePortal: true,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            aria-label="pin note disabled"
            disabled
            sx={{
              padding: "4px",
              color: "rgba(64, 60, 111, 0.3)",
              marginLeft: "-8px",
              cursor: "default",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <img
              src={mdiPin}
              alt="Pin Note Disabled"
              style={{
                width: "20px",
                height: "15px",
                opacity: 0.5,
              }}
            />
          </IconButton>
          <Box
            sx={{
              fontFamily: "Manrope, sans-serif",
              fontSize: "14px",
              color: "rgba(64, 60, 111, 0.5)",
              fontWeight: 400,
            }}
          >
            Pin Note
          </Box>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton
        aria-label={isPinned ? "unpin note" : "pin note"}
        onClick={handlePinClick}
        disabled={hasPinnedNote && !initialData?.pinNote}
        sx={{
          padding: "4px",
          color: isPinned ? "#403C6F" : "rgba(64, 60, 111, 0.5)",
          "&:hover": {
            backgroundColor: "transparent",
          },
          marginLeft: "-8px",
          cursor: "pointer",
          opacity: 1,
        }}
      >
        {isPinned ? (
          <PushPinIcon
            style={{
              width: "20px",
              height: "20px",
              color: "#403C6F",
              opacity: 1,
            }}
          />
        ) : (
          <img
            src={mdiPin}
            alt="Pin Note"
            style={{
              width: "20px",
              height: "20px",
              opacity: initialData?.pinNote ? 0.7 : 0.5,
            }}
          />
        )}
      </IconButton>
      <Box
        sx={{
          fontFamily: "Manrope, sans-serif",
          fontSize: "14px",
          color: isPinned ? "#403C6F" : "rgba(64, 60, 111, 0.8)",
          fontWeight: isPinned ? 600 : 500,
          ml: "4px",
        }}
      >
        {isPinned ? "Pinned" : "Pin Note"}
      </Box>
    </Box>
  );
};

PinAction.propTypes = {
  isPinned: PropTypes.bool.isRequired,
  handlePinClick: PropTypes.func.isRequired,
  hasPinnedNote: PropTypes.bool,
  initialData: PropTypes.object,
};

const NoteInputArea = ({
  isAddNote,
  noteDetails,
  handleInputChange,
  handlePaste,
  handleDrop,
  contentEditableRef,
  error,
}) => {
  React.useEffect(() => {
    if (
      contentEditableRef.current &&
      contentEditableRef.current.textContent !== noteDetails
    ) {
      contentEditableRef.current.textContent = noteDetails;
    }
  }, [noteDetails, contentEditableRef]);

  const dateStr = new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  let borderColor = "#E0E0E0";
  if (isAddNote) {
    borderColor = "#403C6F";
  }

  const handlers = isAddNote
    ? {
        onPaste: handlePaste,
        onDrop: handleDrop,
        onInput: (e) => handleInputChange(e.currentTarget.textContent),
      }
    : {
        onPaste: (e) => e.preventDefault(),
        onDrop: (e) => e.preventDefault(),
        onInput: undefined,
      };

  const inputStyles = {
    minHeight: "190px",
    maxHeight: "200px",
    padding: "12px 16px",
    backgroundColor: isAddNote ? "#FFFFFF" : "#F5F5F5",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    outline: "none",
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    "&:focus": {
      borderColor,
    },
    "&:empty:before": {
      content: isAddNote ? '"Enter your note here..."' : '""',
      color: isAddNote ? placeholderText : "transparent",
      pointerEvents: "none",
    },
    color: isAddNote ? "inherit" : "#666666",
    cursor: isAddNote ? "text" : "default",
    userSelect: "auto",
    opacity: isAddNote ? 1 : 0.8,
    pointerEvents: "auto",
  };

  return (
    <Box sx={{ position: "relative", flex: 1 }}>
      <Box
        sx={{
          color: "#3A3765",
          fontSize: "0.875rem",
          mb: 1,
          ml: 0.2,
          fontFamily: "Manrope, sans-serif",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span style={{ fontWeight: 500 }}>Date:</span>
        <span style={{ fontWeight: 600 }}>{dateStr}</span>
      </Box>
      <Box sx={{ position: "relative" }}>
        <Box
          contentEditable={isAddNote}
          ref={contentEditableRef}
          {...handlers}
          sx={inputStyles}
        />
      </Box>
      {error && (
        <Box
          sx={{
            color: "#d32f2f",
            fontSize: "0.75rem",
            marginTop: "4px",
            marginLeft: "14px",
          }}
        >
          {error}
        </Box>
      )}
    </Box>
  );
};

NoteInputArea.propTypes = {
  isAddNote: PropTypes.bool.isRequired,
  noteDetails: PropTypes.string.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handlePaste: PropTypes.func.isRequired,
  handleDrop: PropTypes.func.isRequired,
  contentEditableRef: PropTypes.object.isRequired,
  error: PropTypes.string,
};

const NoteFooter = ({
  onClose,
  handleSubmit,
  loading,
  isSaving,
  isSaveEnabled,
  isAddNote,
  noteDetails,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      padding: "0 16px 16px",
      marginTop: "0",
      paddingTop: "8px",
    }}
  >
    <Box sx={{ display: "flex", gap: "16px" }}>
      <Button
        onClick={onClose}
        sx={{
          color: "#403C6F",
          backgroundColor: "#FFFFFF",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "16px",
          fontStyle: "semibold",
          padding: "12px 24px",
          borderRadius: "50px",
          "&:hover": {
            backgroundColor: "rgba(64, 60, 111, 0.04)",
          },
        }}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        sx={{
          backgroundColor: isSaving ? "#A0A0A0" : "#403C6F",
          color: "#FFFFFF",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "16px",
          fontStyle: "semibold",
          padding: "12px 24px",
          borderRadius: "50px",
          "&:hover": {
            backgroundColor: isSaving ? "#A0A0A0" : "#4a4576",
          },
          "&.Mui-disabled": {
            backgroundColor: "#E0E0E0",
            color: "#9E9E9E",
          },
        }}
        disabled={
          loading ||
          isSaving ||
          !isSaveEnabled ||
          (isAddNote &&
            (!noteDetails.trim() || !/[a-zA-Z0-9]/.test(noteDetails)))
        }
      >
        {loading || isSaving ? "Saving..." : "Save"}
      </Button>
    </Box>
  </Box>
);

NoteFooter.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isSaving: PropTypes.bool.isRequired,
  isSaveEnabled: PropTypes.bool.isRequired,
  isAddNote: PropTypes.bool.isRequired,
  noteDetails: PropTypes.string.isRequired,
};

const AddNoteModal = ({
  open,
  onClose,
  onSave,
  hasPinnedNote,
  initialData,
  programOptions = {},
  canDelete = false,
  loading = false,
}) => {
  const [selectedType, setSelectedType] = useState(
    initialData?.noteTypes?.[0] || initialData?.type || "",
  );
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [noteDetails, setNoteDetails] = useState(
    initialData?.noteDetails ||
      initialData?.note ||
      initialData?.content ||
      initialData?.noteEntry ||
      "",
  );
  const [error, setError] = useState("");

  const [isPinned, setIsPinned] = useState(initialData?.pinNote || false);
  const [isArchived, setIsArchived] = useState(
    initialData?.isArchived || false,
  );
  const [selectedProgram, setSelectedProgram] = useState(
    initialData?.programName || "",
  );
  const [selectedProgramName, setSelectedProgramName] = useState(
    initialData?.programName || "",
  );
  const contentEditableRef = React.useRef(null);
  console.log("can del", canDelete);
  /** @type {boolean} Whether this is an "Add Note" modal (vs. edit) */
  const isAddNote = !initialData;
  /** @type {[boolean, Function]} Whether the save button should be enabled */
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  /** Prevent duplicate submissions by disabling Save after first click */
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Handles changes to the note content
   * @param {string} newNoteDetails - The updated note content
   */
  const handleInputChange = (newNoteDetails = "", isPaste = false) => {
    const value = newNoteDetails;
    setError("");
    setNoteDetails(value);
    checkIfChangesMade(
      value,
      selectedType,
      selectedProgramName,
      selectedRatings,
      isPinned,
    );
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    const files = e.clipboardData?.files || [];
    const hasImage =
      Array.from(items).some((i) => i.type?.startsWith("image/")) ||
      Array.from(files).some((f) => f.type?.startsWith("image/"));
    if (hasImage) {
      e.preventDefault();
      setError(
        "Invalid input: Images are not permitted. Provide text content only.",
      );
      return;
    }
    const text = e.clipboardData?.getData("text/plain") || "";
    if (text) {
      e.preventDefault();
      const sel = globalThis.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      } else if (contentEditableRef.current) {
        contentEditableRef.current.appendChild(document.createTextNode(text));
      }
      const newText = contentEditableRef.current?.textContent || "";
      setError(null);
      handleInputChange(newText, true);
    }
  };

  const handleDrop = (e) => {
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      e.preventDefault();
      setError("File attachments are not allowed in notes.");
    }
  };

  /**
   * Checks if there are unsaved changes in the form
   * @param {string} newNoteDetails - Current note content
   * @param {string} newSelectedType - Current note type
   * @param {string} newSelectedProgramName - Current program name
   * @param {string[]} newSelectedRatings - Current ratings
   * @param {boolean} newIsPinned - Current pinned state
   */
  const checkIfChangesMade = (
    details,
    type,
    program,
    ratings,
    pinned,
    archived = isArchived,
  ) => {
    const initialContent =
      initialData?.noteDetails ||
      initialData?.note ||
      initialData?.content ||
      initialData?.noteEntry ||
      "";

    const hasChanges =
      details !== initialContent ||
      type !== (initialData?.noteTypes?.[0] || initialData?.type) ||
      program !== initialData?.programName ||
      JSON.stringify(ratings) !==
        JSON.stringify(
          initialData?.rating
            ? [`${initialData.rating} Star${initialData.rating > 1 ? "s" : ""}`]
            : [],
        ) ||
      pinned !== initialData?.pinNote ||
      archived !== initialData?.archived;

    setIsSaveEnabled(hasChanges);
  };

  const location = useLocation();
  const path = location?.pathname || "";
  const isProfilePath =
    path.includes("providerProfile") || path.includes("pharmacyProfile");
  const hideProgramSelect = isProfilePath;

  const validateNote = (content) => {
    if (!content.trim()) {
      return "Note cannot be empty or contain only spaces";
    }
    if (!/[a-zA-Z0-9]/.test(content)) {
      return "Note must contain at least one letter or number";
    }
    return null;
  };

  const parseRatingFromStarText = (value) => {
    if (typeof value !== "string") {
      return null;
    }

    const lower = value.toLowerCase();
    const idx = lower.indexOf("star");
    if (idx <= 0) {
      return null;
    }

    let i = idx - 1;
    while (i >= 0 && lower[i] === " ") i--;

    let numStr = "";
    while (i >= 0 && lower[i] >= "0" && lower[i] <= "9") {
      numStr = lower[i] + numStr;
      i--;
    }

    return numStr ? Number.parseInt(numStr, 10) : null;
  };

  const getRatingNumber = () => {
    if (selectedRatings.length > 0) {
      const ratingStr = selectedRatings[0];
      return typeof ratingStr === "string"
        ? Number.parseInt(ratingStr.split(" ")[0], 10)
        : ratingStr;
    }

    return parseRatingFromStarText(initialData?.noteEntry);
  };

  const handleSubmit = () => {
    if (isSaving) return false;

    setError("");
    const noteContent = noteDetails || "";
    const validationError = validateNote(noteContent);
    if (validationError) {
      setError(validationError);
      return false;
    }

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    const ratingNumber = getRatingNumber();
    const noteType = selectedType || initialData?.type || "General";

    let noteEntry = "";
    if (ratingNumber) {
      const starText = ratingNumber > 1 ? "stars" : "star";
      noteEntry = `${ratingNumber} ${starText}`;
    }

    const noteData = {
      noteTypes: [noteType],
      type: noteType,
      noteDetails: noteDetails || initialData?.noteDetails || "",
      noteEntry,
      pinNote: isPinned,
      pinned: isPinned,
      archived: isArchived,
      status: isArchived ? "ARCHIVED" : "CREATED",
      editedAt: initialData ? formattedDate : null,
      editedDate: initialData ? formattedDate : null,
      rating: ratingNumber,
      programId: hideProgramSelect ? null : selectedProgram,
      programName: hideProgramSelect ? null : selectedProgramName,
    };

    if (onSave) {
      setIsSaving(true);
      const result = onSave(noteData);

      if (result && typeof result.then === "function") {
        result.catch((error) => {
          // Save operation failed; reset saving state
          if (error instanceof Error) {
            // Error handled by resetting saving state
          }
          setIsSaving(false);
        });
      }

      return true;
    }
    return false;
  };

  const extractRatingNumber = (data) => {
    if (!data) return null;

    if (data.rating) {
      return typeof data.rating === "string"
        ? Number.parseInt(data.rating, 10)
        : data.rating;
    }

    if (typeof data.noteEntry === "string") {
      // Check for 'star' (case-insensitive) and digits before it, no regex
      const lower = data.noteEntry.toLowerCase();
      const idx = lower.indexOf("star");
      if (idx > 0) {
        // Check if there is a digit before 'star'
        let i = idx - 1;
        // Skip spaces
        while (i >= 0 && lower[i] === " ") i--;
        if (i >= 0 && lower[i] >= "0" && lower[i] <= "9") {
          // There is at least one digit before 'star'
          return 1; // We know there is a digit, but to get the actual number, parse as before
        }
      }
    }

    return null;
  };

  const resetForm = () => {
    setSelectedType("");
    setSelectedProgram("");
    setSelectedRatings([]);
    setSelectedProgramName("");
    setNoteDetails("");
    setIsPinned(false);
    setIsArchived(false);
    if (contentEditableRef.current) {
      contentEditableRef.current.textContent = "";
    }
  };

  const initializeFormWithData = () => {
    const type = initialData.noteTypes?.[0] || initialData.type || "";
    setSelectedType(type);
    setIsPinned(initialData?.pinNote || false);
    setIsArchived(initialData?.isArchived || false);

    const content =
      initialData.noteDetails ||
      initialData.note ||
      initialData.content ||
      initialData.noteEntry ||
      "";
    setNoteDetails(content);
    if (contentEditableRef.current) {
      contentEditableRef.current.textContent = content;
    }

    if (type === "Testimonial") {
      if (initialData.programName) {
        setSelectedProgramName(initialData.programName);
        const programEntry = Object.entries(programOptions || {}).find(
          ([, name]) => name === initialData.programName,
        );
        if (programEntry) {
          setSelectedProgram(programEntry[0]);
        }
      } else {
        setSelectedProgramName("");
        setSelectedProgram("");
      }

      const ratingNum = extractRatingNumber(initialData);
      if (ratingNum) {
        setSelectedRatings([`${ratingNum} Star${ratingNum > 1 ? "s" : ""}`]);
      } else {
        setSelectedRatings([]);
      }
    } else {
      setSelectedProgramName("");
      setSelectedProgram("");
      setSelectedRatings([]);
    }
  };

  React.useEffect(() => {
    if (open) {
      setError("");

      if (initialData) {
        initializeFormWithData();
      } else {
        resetForm();
      }

      setIsSaveEnabled(false);
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData, programOptions]);
  const dialogRef = React.useRef(null);

  React.useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.removeAttribute("aria-hidden");
      const hiddenDescendants = dialogRef.current.querySelectorAll(
        '[aria-hidden="true"]',
      );
      for (const el of hiddenDescendants) {
        if (
          el.querySelector("button,[href],input,select,textarea,[tabindex]")
        ) {
          el.removeAttribute("aria-hidden");
        }
      }
    }
  }, [open]);

  if (!open) return null;
  const handleToggleArchive = () => {
    const newArchivedState = !isArchived;
    setIsArchived(newArchivedState);
    checkIfChangesMade(
      noteDetails,
      selectedType,
      selectedProgramName,
      selectedRatings,
      isPinned,
      newArchivedState,
    );
  };

  const handlePinClick = () => {
    if (!hasPinnedNote || initialData?.pinNote) {
      const newPinnedState = !isPinned;
      setIsPinned(newPinnedState);
      checkIfChangesMade(
        noteDetails,
        selectedType,
        selectedProgramName,
        selectedRatings,
        newPinnedState,
        isArchived,
      );
    }
  };

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      onClose={onClose}
      keepMounted={false}
      maxWidth="md"
      slots={{
        transition: undefined, // Use default transition
      }}
      slotProps={{
        transition: {
          onEntering: (node) => {
            node.removeAttribute("aria-hidden");
          },
          onEntered: (node) => {
            node.removeAttribute("aria-hidden");
          },
        },
        paper: {
          sx: {
            width: { xs: "95%", md: "900px" },
            maxWidth: "none",
            height: hideProgramSelect ? "500px" : "490px",
            borderRadius: "16px",
            padding: hideProgramSelect
              ? { xs: "16px", md: "16px" }
              : { xs: "16px", md: "20px" },
            display: "flex",
            flexDirection: "column",
            gap: hideProgramSelect ? "12px" : "16px",
            backgroundColor: "#F7F8FB",
            boxShadow: "none",
            border: "none",
            transition: "height 0.2s ease, padding 0.2s ease",
            "& .MuiDialog-paper": {
              boxShadow: "none",
              border: "none",
              backgroundColor: "#F7F8FB",
            },
          },
        },
      }}
    >
      <Box sx={{ padding: 0, margin: 0, position: "relative" }}>
        <DialogTitle
          sx={{
            color: "#10100F",
            padding: 0,
            margin: 0,
            fontWeight: 700,
            fontSize: "1.5rem",
            lineHeight: 1.2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "& .MuiTypography-root": {
              fontSize: "1.5rem",
              fontWeight: 700,
              color: "#10100F",
            },
          }}
        >
          {initialData ? "Edit Note" : "Add Note"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#080A0E",
              "&:hover": {
                backgroundColor: "#F9F9F9",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <HeaderActions
            isAddNote={isAddNote}
            isArchived={isArchived}
            handleToggleArchive={handleToggleArchive}
            canDelete={canDelete}
            isPinned={isPinned}
            handlePinClick={handlePinClick}
            hasPinnedNote={hasPinnedNote}
            initialData={initialData}
          />
        </Box>
      </Box>

      <DialogContent sx={{ padding: 0, flex: 1, overflow: "hidden" }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: hideProgramSelect ? "16px" : "24px",
            minHeight: "auto",
            display: "flex",
            flexDirection: "column",

            overflow: "hidden",
            border: "1px solid #E9E9E8",
          }}
        >
          <NoteInputArea
            isAddNote={isAddNote}
            noteDetails={noteDetails}
            handleInputChange={handleInputChange}
            handlePaste={handlePaste}
            handleDrop={handleDrop}
            contentEditableRef={contentEditableRef}
            error={error}
            isArchived={isArchived}
          />
        </Box>
      </DialogContent>

      <NoteFooter
        onClose={onClose}
        handleSubmit={handleSubmit}
        loading={loading}
        isSaving={isSaving}
        isSaveEnabled={isSaveEnabled}
        isAddNote={isAddNote}
        noteDetails={noteDetails}
      />
    </Dialog>
  );
};

export default AddNoteModal;

AddNoteModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  hasPinnedNote: PropTypes.bool,
  initialData: PropTypes.object,
  programOptions: PropTypes.object,
  canDelete: PropTypes.bool,
  loading: PropTypes.bool,
};
